import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart3 } from 'lucide-react';

import PrivateLayout from '../../layouts/PrivateLayout';
import { cn } from '../../../lib/utils';
import { useUser } from '../../../contexts/UserContext';
import LeadsDashboard from './dashboard/LeadsDashboard';
import LeadHistory from './LeadHistory';
import SalesTeamTab from './teams/SalesTeamTab';
import OnboardingTeamTab from './teams/OnboardingTeamTab';
import { CARD_SHADOW } from './teams/premium';
import { PeriodBar, currentPeriod, customKey } from './teams/teamUi';

// Lead Metrics (Nic 2026-08-23): the one numbers-and-records destination in
// Operations. Four tabs, four questions:
//   Funnel          — how are leads converting across the WHOLE journey
//   Sales Team      — how the Sales Reps are doing against the Objectives
//                     (speed to lead, agreements, live hot seats, bonus)
//   Onboarding Team — how the Onboarding Specialists are doing (FOTCs,
//                     VA.gov, where clients stall, bonus)
//   History         — the record of every past lead
// The team tabs replaced the per-touch Cadence scorecard (Nic 2026-10-01)
// and carry the quarterly bonus tiers, so they gate on
// onboarding_board:metrics: admins and the Onboarding Manager only.
//
// Layout (Nic 2026-10-25): the /billing/envoy page exactly: the top bar
// spans the full width (title left, tabs center, dates right) and the
// content sits in a centered max-w-6xl column. On a phone the tabs use
// short names and share the width evenly instead of wrapping.

const TabButton = ({ active, onClick, label, short }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      // Text color only: fading the background left the old tab's blue
      // behind for a beat on every switch (Nic 2026-10-05).
      'flex-1 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-sm font-medium outline-none transition-[color] focus-visible:ring-2 focus-visible:ring-sky-400/60 sm:flex-none sm:px-4',
      active
        ? 'bg-primary text-primary-foreground shadow-sm'
        : 'text-slate-500 hover:text-slate-800',
    )}
  >
    <span className="sm:hidden">{short}</span>
    <span className="hidden sm:inline">{label}</span>
  </button>
);

// Old links (?tab=cadence, the /operations/onboarding-metrics redirect) land
// on the Sales Team tab.
const LEGACY_TAB = { cadence: 'sales' };

const LeadMetricsPage = () => {
  const { hasPermission } = useUser();
  const canLeads = hasPermission('leads:view') || hasPermission('leads:manage');
  const canTeams = hasPermission('onboarding_board:metrics');

  const tabs = [
    canLeads && { key: 'funnel', label: 'Funnel', short: 'Funnel' },
    canTeams && { key: 'sales', label: 'Sales Team', short: 'Sales' },
    canTeams && {
      key: 'onboarding',
      label: 'Onboarding Team',
      short: 'Onboarding',
    },
    canLeads && { key: 'history', label: 'History', short: 'History' },
  ].filter(Boolean);

  const [params] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const raw = params.get('tab');
    const requested = LEGACY_TAB[raw] || raw;
    return (
      tabs.find((t) => t.key === requested)?.key || tabs[0]?.key || 'funnel'
    );
  });

  // One period for every dated tab (Nic 2026-10-25): Year, Quarter, Month or
  // Custom, and flipping between tabs keeps it. Picking an event on the
  // Funnel jumps the period to that event's dates; `jumpedFrom` remembers
  // where you were, and leaving Events goes back there.
  const [period, setPeriod] = useState(() => currentPeriod('quarter'));
  const [jumpedFrom, setJumpedFrom] = useState(null);
  // The Source menu (Funnel and both team tabs, 2026-10-25), kept across
  // tabs like the period: pick Envoy on the Funnel, Sales Team shows Envoy.
  const [filter, setFilter] = useState({ source: '', partnerId: '' });

  const periodBar = (
    <PeriodBar
      value={period}
      onChange={(next) => {
        setJumpedFrom(null);
        setPeriod(next);
      }}
    />
  );
  const onWindow = (range) => {
    if (range) {
      setJumpedFrom((was) => was || period);
      setPeriod(customKey(range.start, range.end));
    } else if (jumpedFrom) {
      setPeriod(jumpedFrom);
      setJumpedFrom(null);
    }
  };
  const sourceProps = { filter, onFilter: setFilter, onWindow, jumped: !!jumpedFrom };

  return (
    // The /billing/envoy canvas (Nic 2026-10-25). `fluid`: this page fits any
    // width itself (wide tables scroll in their own box), so the shell must
    // not grow to its content. Without it the charts deadlocked: a chart
    // sizes to its box, the shell grew the box to the chart, and after a
    // resize the page stayed wider than the window with a sideways scroll.
    <PrivateLayout background="#F5F7FA" fluid>
      <div className="w-full">
        {/* The /billing/envoy top bar, across the full width like that page
            (Nic 2026-10-25); only the content below sits in the centered
            max-w-6xl column. Title left, tabs dead center, the dates right.
            The right column never shrinks below its controls (the title's
            subtitle gives way first), so nothing scrolls sideways; below xl
            it stacks, centered. */}
        <div className="mb-4 grid grid-cols-1 items-center justify-items-center gap-3 xl:grid-cols-[minmax(0,1fr)_auto_minmax(max-content,1fr)] xl:justify-items-stretch">
          {/* Flex alignment, not text-center: Bootstrap's .text-center is
              !important and beat xl:text-left, which held the title in the
              middle of its column on wide screens. */}
          <div className="flex min-w-0 max-w-full flex-col items-center xl:items-start">
            <h2 className="mb-0 inline-flex items-center gap-2 whitespace-nowrap text-xl font-bold text-slate-800">
              <BarChart3 size={19} className="text-slate-400" />
              Lead Metrics
            </h2>
            <div className="truncate text-xs text-slate-400">
              The funnel, both teams, and the record
            </div>
          </div>
          <nav
            aria-label="Lead Metrics tabs"
            className={cn(
              'flex w-full rounded-xl bg-white p-1 sm:w-auto xl:justify-self-center',
              CARD_SHADOW,
            )}
          >
            {tabs.map((t) => (
              <TabButton
                key={t.key}
                active={tab === t.key}
                onClick={() => setTab(t.key)}
                label={t.label}
                short={t.short}
              />
            ))}
          </nav>
          <div className="flex min-w-0 justify-center xl:justify-end">
            {tab !== 'history' ? periodBar : null}
          </div>
        </div>

        <div className="mx-auto max-w-6xl pb-10 pt-1">
          {tab === 'sales' ? (
            <SalesTeamTab period={period} {...sourceProps} />
          ) : tab === 'onboarding' ? (
            <OnboardingTeamTab period={period} {...sourceProps} />
          ) : tab === 'history' ? (
            <LeadHistory heightClass="lg:h-[calc(100vh-150px)]" />
          ) : (
            <LeadsDashboard period={period} {...sourceProps} />
          )}
        </div>
      </div>
    </PrivateLayout>
  );
};

export default LeadMetricsPage;
